import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { OfficeSelectorService, UserInfoService } from '@tt-chvac/core-sales-web';
import { UserService } from '../../../modules/jobs-list-master/services/user.service';
import { IDashboardPreference } from '../../../shared/model/dashboard-preferences';
import { ITileInfo, TileService, TileTypes } from '../../../shared/services/tile.service';
import { OrderBookingService } from '@tt-chvac/order';
import { ToasterService, ToasterType } from '@tt-chvac/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { RefreshService } from '@tt-chvac/core';
import { AppConstants } from '../../../shared/constants/constants';

export interface ITileInfoWithOrder extends ITileInfo {
  order: number;
}

@Component({
    selector: 'app-home',
    templateUrl: './home-dashboard.component.html',
    styleUrls: ['./home-dashboard.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class HomeDashboardComponent implements OnDestroy, OnInit {
  private userRoles: string[];
  private userPreferences: IDashboardPreference;
  private subscriptions: Subscription[] = [];

  public TileType = TileTypes;
  public drAddressId: number;
  public userTiles: ITileInfoWithOrder[];
  public onlyShowMyRequests = true;
  public exportReturnReasons: boolean;

  constructor(
    private userInfoService: UserInfoService,
    private tileService: TileService,
    private userService: UserService,
    private officeSelectorService: OfficeSelectorService,
    private orderBookingService: OrderBookingService,
    private toasterService: ToasterService,
    private refreshService: RefreshService,
    private appConstants: AppConstants,
    private router: Router) {
  }

  ngOnInit(): void {
    this.userRoles = this.userInfoService.getUserRoles() ?? [];
    this.exportReturnReasons = false;

    this.subscriptions.push(this.userService.getDashboardPreferences().subscribe(prefs => {
      this.userPreferences = prefs ?? { onlyShowMyRequests: true, tiles: [] };
      this.onlyShowMyRequests = true;
      this.userTiles = this.getUserTiles();
    }));

    this.subscriptions.push(this.officeSelectorService.getSelectedOffice().subscribe(
      selectedOffice => {
        this.drAddressId = selectedOffice.drAddressId;
        // Calling to set url/route params so other tabs that call this method and depend on route params stay in sync with our choice
        this.router.navigate([`/home/${selectedOffice.drAddressId}`]);
      }
    ));

    this.subscriptions.push(this.refreshService.refreshSubject.subscribe(status => {
      if (status === this.appConstants.ONLY_SHOW_MY_REQUESTS_STATUS) {
        this.userService.getDashboardPreferences().subscribe(prefs => {
          this.userPreferences = prefs ?? { onlyShowMyRequests: true, tiles: [] };
          this.onlyShowMyRequests = this.userPreferences?.onlyShowMyRequests;
        });
      }
    }));

  }

  ngOnDestroy(): void {
    while (this.subscriptions.length > 0) {
      const subscription = this.subscriptions.shift();
      subscription.unsubscribe();
    }
  }

  private getUserTiles(): ITileInfoWithOrder[] {
    return this.tileService.getUserAccessibleTiles(this.userRoles).filter((tile) => {
      // filter out hidden tiles
      const tilePref = this.userPreferences.tiles.find(t => t.name === tile.type);
      return tilePref?.isVisible !== undefined ? tilePref.isVisible : true;
    }).map((tile) => {
      // add saved order from preference
      const tilePref = this.userPreferences.tiles.find(t => t.name === tile.type);
      (tile as ITileInfoWithOrder).order = tilePref?.sequence ? tilePref.sequence : 0;
      return tile as ITileInfoWithOrder;
    });
  }

  public onExportClick(tileType: TileTypes) {
    if (tileType === TileTypes.CreditApprovalBacklog) {
      const callerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.orderBookingService.requestCreditBacklogExport(callerTimeZone).subscribe({
        next: (result) => {
          if (result) {
            this.toasterService.setToaster(ToasterType.INFO, 'Your export has been requested, a notification will be sent when it is complete and you can download.');
          } else {
            this.toasterService.setToaster(ToasterType.ERROR, 'Something went wrong with the export request.');
          }
        },
        error: (response: HttpErrorResponse) => {
          this.toasterService.setToaster(ToasterType.ERROR, response.error);
        }
      });
    } else if (tileType === TileTypes.ReturnReasonCounts) {
      this.exportReturnReasons = !this.exportReturnReasons;
    }
  }
}
